import {
	Box,
	Button,
	Center,
	createStyles,
	Icon,
	TFCard,
	TFDataTable,
	TFEmptyStateMessage,
	TFText,
	Title,
	UnstyledButton,
	useDisclosure,
} from '@tf/ui';
import { getObjectDisplayName, splitObjectId } from '@tf/utils';
import { Link } from 'react-router-dom';
import { CaseStatusBadge } from '@/components/shared';
import { useProfileCases } from '@/core/api/caseRequests';
import { useSelectedAccount } from '@/core/hooks';
import { useGetAuthorName } from '@/core/utils';
import { paths } from '@/routes/paths';

import { CreateCaseModal } from './CreateCaseModal';
import { clientCaseKindLabels, clientCaseKindOptions } from './requestKind';

const useStyles = createStyles(({ colors }) => ({
	link: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
}));

const ClientCasesTableError = () => {
	return (
		<TFCard>
			<Center h={300}>
				<TFEmptyStateMessage
					icon={<Icon.IconUnlink />}
					title="Something went wrong"
					description="Try to refresh the page or come back later"
					primaryAction={
						<Button
							variant="light"
							size="md"
							leftSection={<Icon.IconRefresh size={18} />}
							onClick={() => window.location.reload()}
						>
							Refresh the page
						</Button>
					}
				/>
			</Center>
		</TFCard>
	);
};

export const CasesTable = () => {
	const { classes } = useStyles();

	const { profileId, meta } = useSelectedAccount();
	const { data, isPending, isError } = useProfileCases(profileId);
	const getAuthorName = useGetAuthorName();

	const tableItems = (data ?? []).map((c) => {
		return {
			...c,
			authorName: getAuthorName(c.case.meta['@author']),
		};
	});

	const createClientCaseModal = useDisclosure();

	return (
		<Box mt="1.5rem">
			{data ? (
				<>
					<Box mb="0.75rem" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Title order={3}>Cases</Title>
						<Button onClick={createClientCaseModal.open}>Create case</Button>
					</Box>
					<TFDataTable
						noDataMessage={{
							icon: <Icon.IconDatabase />,
							title: 'Cases',
							description: 'Cases allow you to request additional data from an end client',
							primaryAction: (
								<Button onClick={createClientCaseModal.open} leftSection={<Icon.IconPlus size={16} />}>
									Create case
								</Button>
							),
						}}
						name="cases-table"
						data={tableItems}
						isLoading={isPending}
						defs={[
							{
								header: 'ID',
								id: 'caseId',
								size: 50,
								accessorFn: (caseObject) => getObjectDisplayName(caseObject.case.aliases),
								Cell: ({ renderedCellValue, row }) => {
									const { objectName: paymentUuid } = splitObjectId(row.original.case.id);
									return (
										<UnstyledButton
											component={Link}
											to={paths.case(meta.graphNodeId, paymentUuid)}
											className={classes.link}
										>
											<TFText inherit>{renderedCellValue ?? 'N/A'}</TFText>
										</UnstyledButton>
									);
								},
							},
							{
								header: 'Type',
								id: 'kind',
								size: 100,
								filterFn: (row, _columnId, filterValue) => {
									return row.original.case.kind === filterValue;
								},
								accessorFn: (caseObject) => clientCaseKindLabels[caseObject.case.kind],
								Cell: ({ renderedCellValue }) => {
									return <TFText inherit>{renderedCellValue}</TFText>;
								},
							},
							{
								header: 'Client email',
								accessorKey: 'case.labels.recipient_email',
								size: 120,
							},
							{
								header: 'Author',
								accessorKey: 'authorName',
								size: 70,
							},
							{
								header: 'Status',
								id: 'status',
								size: 70,
								Cell: ({ row: { original } }) => {
									return (
										<CaseStatusBadge
											caseStatus={original.case.labels.status}
											caseRequestStatus={original.request.labels.status}
										/>
									);
								},
							},
						]}
						availableFilters={[
							{
								name: 'labels.status',
								label: 'Status',
								type: 'select',
								value: [
									{ value: 'ACTIVE', label: 'Active' },
									{ value: 'CLOSED', label: 'Closed' },
								],
							},
							{
								name: 'kind',
								label: 'Type',
								type: 'select',
								value: clientCaseKindOptions,
							},
						]}
					/>
				</>
			) : isError ? (
				<ClientCasesTableError />
			) : null}

			<CreateCaseModal
				onClose={createClientCaseModal.close}
				opened={createClientCaseModal.isOpen}
				title="Create a new case"
				contentProps={{
					close: createClientCaseModal.close,
				}}
			/>
		</Box>
	);
};
